import { z } from "zod";

export const slugSchema = z
  .string({ error: "slug は文字列で指定してください" })
  .min(1, "slug は必須です")
  .max(80, "slug は 80 文字以下にしてください")
  .regex(
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
    "slug は英小文字・数字・ハイフンのみ使えます (先頭末尾はハイフン不可)",
  );

export const statusSchema = z.enum(["draft", "published", "archived"], {
  error: "status は draft / published / archived のいずれかです",
});

// Accept a string up to `max`, an empty string (treated as clear), or null.
// Optional at the field level so PATCH callers can omit fields entirely.
const nullableMaxString = (max: number) =>
  z.union([z.string().max(max), z.literal(""), z.null()]).optional();

// Deploy-hook style URLs only — restrict scheme to http(s) so a malicious
// owner can't smuggle file:// / javascript: / data: into Workers fetch().
// Even though Cloudflare's fetch rejects most of these at runtime, failing at
// schema time gives a useful 400 instead of an opaque 502.
const nullableUrl = (max: number) =>
  z
    .union([
      z
        .url()
        .max(max)
        .refine(
          (s) => /^https?:\/\//i.test(s),
          { message: "URL は http:// または https:// で始まる必要があります" },
        ),
      z.literal(""),
      z.null(),
    ])
    .optional();

export const updateProjectSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  deployHookUrl: nullableUrl(2048),
  frontmatterTemplate: nullableMaxString(8000),
});

export const deleteProjectSchema = z.object({
  confirmSlug: slugSchema,
});

export const createApiKeySchema = z.object({
  name: z.string().min(1).max(120),
  expiresAt: z.iso.datetime({ offset: true }).optional(),
});

export const uiModeSchema = z.enum(["simple", "advanced"]);

export const updateUiModeSchema = z.object({
  uiMode: uiModeSchema,
});

export const memberRoleSchema = z.enum(["owner", "member"], {
  error: "role は owner / member のいずれかです",
});

// Invite emails are stored as trimmed lowercase. Better Auth lowercases the
// address before every lookup (sign-in and magic-link verify), so a row saved
// with any uppercase character could never sign in, and an exact-match lookup
// would create a second row for the same inbox.
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(320)
  .pipe(z.email({ error: "メールアドレスの形式が正しくありません" }));

// Adds a person (new or existing) to a project. `role` defaults to member.
export const inviteMemberSchema = z.object({
  email: emailSchema,
  name: z.string().min(1).max(120).optional(),
  role: memberRoleSchema.default("member"),
});

export const updateMemberRoleSchema = z.object({
  role: memberRoleSchema,
});

export const MAX_CONTENT_BODY_LENGTH = 50_000;

// Cap on a single AI chat message. Shared so the schema, the client-side
// length guard, and the textarea maxLength stay in lockstep.
export const MAX_USER_MESSAGE_LENGTH = 2000;

export const aiChatTurnSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(4000),
});

export const aiRewriteRequestSchema = z.object({
  // Current full body (frontmatter + markdown). Defensive cap; LP bodies are
  // small in practice. The provider would reject larger anyway.
  body: z.string().max(MAX_CONTENT_BODY_LENGTH),
  // Recent turns only — the SPA prunes; the API truncates again as a guard.
  history: z.array(aiChatTurnSchema).max(6).default([]),
  userMessage: z.string().min(1).max(MAX_USER_MESSAGE_LENGTH),
});

export const frontmatterSchema = z.object({
  title: z
    .string({ error: "title は文字列で指定してください" })
    .min(1, "title は必須です")
    .max(300, "title は 300 文字以下にしてください"),
  // SSG LP 用途を主眼に置いているため未指定なら published 扱い。
  // 寝かせたい時だけ frontmatter に明示的に "draft" を書く。
  status: statusSchema.default("published"),
  tags: z
    .array(
      z
        .string({ error: "tag は文字列で指定してください" })
        .min(1, "tag は空にできません")
        .max(60, "tag は 60 文字以下にしてください"),
      { error: "tags は文字列の配列で指定してください" },
    )
    .default([]),
  // offset: true — offset forms like 2026-06-10T21:00:00+09:00 (JST) are
  // valid ISO 8601 too; the default accepts only Z, contradicting the error
  // message. Timezone-less local datetimes stay rejected (their meaning would
  // depend on the runtime's zone).
  publishedAt: z.iso
    .datetime({
      offset: true,
      error: "publishedAt は ISO 8601 形式の日時で指定してください",
    })
    .optional(),
  // Author-defined rule for the simple-mode AI chat editor. Not read by the
  // frontend; the AI route elevates it into the system prompt. Optional; the
  // 2000-char cap guards the prompt budget (body is already 50k-capped).
  aiInstructions: z
    .string()
    .max(2000, "aiInstructions は 2000 文字以下にしてください")
    .optional(),
  // slug は frontmatter からは指定不可 (URL path が唯一の正)。書かれても zod
  // strip で黙って捨てる。
});

export type Frontmatter = z.infer<typeof frontmatterSchema>;
export type ContentStatus = z.infer<typeof statusSchema>;
export type UiMode = z.infer<typeof uiModeSchema>;
export type AiChatTurn = z.infer<typeof aiChatTurnSchema>;
